import { Dispatch, SetStateAction } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { MessageType } from '../../common/types';
import useChat from '../../hooks/use-chat';
import { cn, shouldUseMaxHeight } from '../../lib/utils';
import { CURRENT_INSTANCE } from '../../tenant';
import TopicLegalName from '../common/topic-legal-name';
import { Badge } from '../ui/badge';

interface ChatShellProps {
  children: React.ReactNode;
  addStaticMessage: (messages: string, type: MessageType) => void;
  setQuery: Dispatch<SetStateAction<string>>;
  onResetChat: () => void;
  topicPickerControls?: React.ReactNode;
}

function ChatShell(props: ChatShellProps) {
  const { t } = useTranslation();
  const { topics, selectedTopic } = useChat();
  const currentTopic = topics.find((item) => item.id === selectedTopic);
  const topicLegalTitle = currentTopic
    ? t('common.selected_topic', { topic: currentTopic.legalName })
    : t('common.no_topic_selected');

  const layoutClasses = CURRENT_INSTANCE.theme.classes?.layout;
  const showResetChatButton = CURRENT_INSTANCE.showResetChatButton !== false;

  return (
    <main
      className={cn(
        'relative overflow-hidden rounded-[28px] border border-slate-200/70 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.08)]',
        shouldUseMaxHeight() && 'max-h-[1000px] min-h-[300px] sm:max-h-[700px] sm:min-h-[200px]',
        layoutClasses?.container
      )}
      data-iframe-size=""
    >
      <div
        className={cn(
          'absolute inset-x-0 top-0 h-28 bg-[radial-gradient(circle_at_top,_hsl(var(--xircle-main))_0%,_transparent_65%)] opacity-20',
          layoutClasses?.headerAccent
        )}
      />
      <header className="relative z-10 flex flex-col gap-2 px-4 pt-5 pb-4 sm:px-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="shrink-0 whitespace-nowrap tracking-normal [&_b]:mr-1">
              <Trans i18nKey="header.title" t={t}>
                <b />
              </Trans>
            </Badge>
          </div>
          <section className="flex flex-wrap items-center gap-2 self-start sm:justify-end">
            {showResetChatButton && (
              <button
                type="button"
                onClick={props.onResetChat}
                className="cursor-pointer rounded-full border border-slate-200/80 bg-white/90 px-3 py-1 text-xs text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-900"
                title={t('chat.reset')}
              >
                {t('chat.reset')}
              </button>
            )}
            <div className="rounded-full border border-slate-200/80 bg-white/90 px-3 py-1 text-xs text-slate-600 shadow-sm">
              <TopicLegalName
                title={topicLegalTitle}
                text={currentTopic?.legalName || topicLegalTitle}
              />
            </div>
            {props.topicPickerControls}
          </section>
        </div>
        <h3
          className={cn(
            'm-0 leading-5 tracking-normal text-slate-700 lg:whitespace-nowrap',
            layoutClasses?.headerSubtitle,
            '!text-[13px] !font-normal sm:!text-sm'
          )}
        >
          {t('header.subtitle')}
        </h3>
      </header>
      <div className="relative z-10 px-4 pb-6 sm:px-6">{props.children}</div>
    </main>
  );
}

export default ChatShell;
